import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { refreshRoles } from "../lib/roles";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Shield, ShieldCheck, X, Loader2, Lock } from "lucide-react";

const BASE_PERMISSIONS = [
  { key: "admin", label: "Administrateur (accès quasi complet)" },
  { key: "chef_projet", label: "Chef de projet (CRUD projets/missions)" },
  { key: "expert", label: "Expert (édition de ses tâches/formulaires)" },
  { key: "client", label: "Client (lecture seule)" },
];

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/roles");
      setRoles(r.data);
      await refreshRoles();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (r) => {
    if (!window.confirm(`Supprimer le rôle "${r.label}" ?`)) return;
    try {
      await api.delete(`/admin/roles/${r.key}`);
      toast.success("Rôle supprimé");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Administration</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Rôles</h1>
          <p className="text-sm text-slate-500 mt-1">Les rôles système ne sont pas modifiables. Vous pouvez créer des rôles personnalisés qui hériteront d'un niveau de permission de base.</p>
        </div>
        <button onClick={() => setModal({ mode: "create" })} className="h-10 px-4 bg-btp-terra hover:bg-btp-terra-hover text-white rounded-md text-sm font-semibold flex items-center gap-2" data-testid="role-create-btn">
          <Plus size={16} /> Nouveau rôle
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</div>
        ) : (
          <table className="btp-table">
            <thead><tr><th></th><th>Libellé</th><th>Clé</th><th>Permission de base</th><th>Description</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.key} data-testid={`role-row-${r.key}`}>
                  <td>
                    {r.builtin ? <Lock size={14} className="text-slate-400" title="Rôle système" /> : <ShieldCheck size={14} className="text-btp-terra" title="Rôle personnalisé" />}
                  </td>
                  <td className="font-medium">{r.label}</td>
                  <td className="text-xs font-mono text-slate-500">{r.key}</td>
                  <td><span className="text-xs px-2 py-0.5 bg-btp-navy/10 text-btp-navy rounded uppercase tracking-wider font-semibold">{r.base_permission}</span></td>
                  <td className="text-sm text-slate-500">{r.description || "—"}</td>
                  <td className="text-right">
                    {!r.builtin && (
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setModal({ mode: "edit", role: r })} title="Modifier" className="p-1.5 hover:bg-slate-100 rounded text-slate-600" data-testid={`role-edit-${r.key}`}><Pencil size={14} /></button>
                        <button onClick={() => onDelete(r)} title="Supprimer" className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`role-delete-${r.key}`}><Trash2 size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && <RoleModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function RoleModal({ modal, onClose, onSaved }) {
  const isCreate = modal.mode === "create";
  const r = modal.role;
  const [form, setForm] = useState(isCreate
    ? { key: "", label: "", base_permission: "expert", description: "" }
    : { label: r.label, base_permission: r.base_permission, description: r.description || "" });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isCreate) {
        await api.post("/admin/roles", form);
        toast.success("Rôle créé");
      } else {
        await api.patch(`/admin/roles/${r.key}`, form);
        toast.success("Rôle modifié");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md border border-slate-200 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="role-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="font-heading font-bold text-base flex items-center gap-2"><Shield size={16} className="text-btp-terra" /> {isCreate ? "Nouveau rôle" : `Modifier "${r.label}"`}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {isCreate && (
            <Field label="Clé (identifiant technique)">
              <input required pattern="^[a-z][a-z0-9_]*$" value={form.key} onChange={set("key")} placeholder="ingenieur_senior" className="input font-mono" data-testid="role-key-input" />
              <div className="text-[10px] text-slate-400 mt-1">Minuscules, chiffres, underscore. Doit commencer par une lettre.</div>
            </Field>
          )}
          <Field label="Libellé affiché"><input required value={form.label} onChange={set("label")} placeholder="Ingénieur Senior" className="input" data-testid="role-label-input" /></Field>
          <Field label="Permission de base">
            <select value={form.base_permission} onChange={set("base_permission")} className="input" data-testid="role-base-permission">
              {BASE_PERMISSIONS.map((bp) => <option key={bp.key} value={bp.key}>{bp.label}</option>)}
            </select>
            <div className="text-[10px] text-slate-400 mt-1">Hérite des permissions de ce rôle système.</div>
          </Field>
          <Field label="Description (facultative)">
            <textarea rows={3} value={form.description} onChange={set("description")} className="input" style={{ height: "auto", padding: "0.5rem 0.75rem" }} data-testid="role-description" />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-10 px-4 border border-slate-200 hover:bg-slate-50 rounded-md text-sm">Annuler</button>
            <button type="submit" disabled={saving} className="h-10 px-4 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-60 text-white rounded-md text-sm font-semibold flex items-center gap-2" data-testid="role-save">
              {saving && <Loader2 size={14} className="animate-spin" />} {isCreate ? "Créer" : "Enregistrer"}
            </button>
          </div>
        </form>
        <style>{`.input{ margin-top:0.375rem; width:100%; height:2.5rem; padding:0 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; } .input:focus{ outline:none; border-color:#1e3a5f; box-shadow:0 0 0 1px #1e3a5f; }`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</label>
      {children}
    </div>
  );
}
